import { useCallback } from "react";
import type { ReactNode } from "react";
import { useLayoutActions, useModal } from "../store";
import type { ModalSize } from "../store/layout";

/**
 * Hook to manage a specific modal
 * @param id - Unique identifier for the modal
 * @returns Object with methods to open, close and check modal state
 *
 * @example
 * ```tsx
 * const confirmModal = useModalManager('confirm-delete');
 *
 * // Open modal
 * confirmModal.open(
 *   <div>Are you sure you want to delete?</div>,
 *   "Confirm Delete"
 * );
 *
 * // Close modal
 * confirmModal.close();
 *
 * // Check if open
 * confirmModal.isOpen
 * ```
 */
export const useModalManager = (id: string) => {
  const modal = useModal(id);
  const { openModal, closeModal, isModalOpen } = useLayoutActions();

  const open = useCallback(
    (content: ReactNode, size?: ModalSize) => {
      openModal(id, content, undefined, size);
    },
    [id, openModal],
  );

  const close = useCallback(() => {
    closeModal(id);
  }, [id]);

  const isOpen = isModalOpen(id);

  return {
    open,
    close,
    isOpen,
    modal,
  };
};
